// =============================================================================
// ScriptEditor — read-only RSL preview with toggle to editable mode
// =============================================================================
import { useState } from 'react';
import { Code2, Check, X, ChevronDown, Pencil } from 'lucide-react';
import { formatRoutineToRSL } from '@/engine/rslFormatter';
import type { RoutineNode } from '../../../../backend/shared/types/index';
import type { RuleSystem } from '../../../../backend/shared/types/index';

interface Props {
  name: string;
  gameSystem: RuleSystem;
  definition: RoutineNode[];
  onScriptChange?: (script: string) => void | Promise<void>;
}

export default function ScriptEditor({ name, gameSystem, definition, onScriptChange }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedScript, setEditedScript] = useState('');

  const rslScript = formatRoutineToRSL(name, gameSystem, definition);
  
  const handleToggleEdit = () => {
    if (!isEditing) {
      setEditedScript(rslScript);
    }
    setIsEditing(!isEditing);
  };
  
  const handleSave = () => {
    if (onScriptChange) {
      onScriptChange(editedScript);
      setIsEditing(false);
    }
  };
  
  return (
    <div className="bg-darkbg-card border border-darkbg-border rounded-lg overflow-hidden
                    hover:border-gold/30 hover:bg-darkbg-card/80 transition-all duration-150">
      {/* Header — click to expand/collapse */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left"
      >
        <div className="flex items-center gap-1.5 flex-1">
          <Code2 className="w-3.5 h-3.5 text-parchment-muted/40 shrink-0" />
          <span className="text-xs text-parchment-muted/60 font-display">Script</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-parchment-muted/30 shrink-0 transition-transform duration-150 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Content — only shown when expanded */}
      {isExpanded && (
        <div className="border-t border-darkbg-border/40 p-3 space-y-2">
          {isEditing ? (
        <div className="space-y-2">
          <textarea
            value={editedScript}
            onChange={(e) => setEditedScript(e.target.value)}
            className="w-full bg-darkbg border border-darkbg-border rounded p-2 text-parchment text-xs
                       font-mono placeholder-parchment-muted/25 focus:outline-none focus:border-gold
                       resize-none"
            rows={12}
            placeholder="Edit routine script here..."
          />
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => setIsEditing(false)}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-parchment-muted/60
                         border border-darkbg-border rounded hover:border-parchment-muted/40 transition-colors"
            >
              <X className="w-3 h-3" /> Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs btn-primary"
            >
              <Check className="w-3 h-3" /> Save
            </button>
          </div>
        </div>
          ) : (
            <pre className="bg-darkbg rounded p-2 text-parchment text-[11px] font-mono
                           overflow-x-auto border border-darkbg-border/40 max-h-48 overflow-y-auto">
              {rslScript}
            </pre>
          )}

          {!isEditing && (
            <button
              onClick={handleToggleEdit}
              className="p-1.5 rounded text-parchment-muted/30 hover:text-gold hover:bg-gold/10 transition-colors"
              title="Edit script"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
